import { useCallback, useEffect, useState } from 'react'
import { Grid } from '@arco-design/web-react'
import { useTranslation } from 'react-i18next'
import SecureVerifyModal from '@/components/Common/SecureVerifyModal'
import { useSecureVerification } from '@/hooks/useSecureVerification'
import { usePersonalSettings } from '@/hooks/usePersonalSettings'
import { useStatusStore } from '@/stores/statusStore'
import { get2FAStatusApi, getPasskeyStatusApi } from '@/api/user'
import AccountOverviewCard from './components/AccountOverviewCard'
import ProfileCard from './components/ProfileCard'
import OAuthBindingsCard from './components/OAuthBindingsCard'
import SecurityCenterCard from './components/SecurityCenterCard'
import SystemAccessTokenCard from './components/SystemAccessTokenCard'
import NotificationSettingsCard from './components/NotificationSettingsCard'
import DangerZoneCard from './components/DangerZoneCard'
import CheckinAffiliateCard from './components/CheckinAffiliateCard'

const { Row, Col } = Grid

export default function PersonalPage() {
  const { t } = useTranslation()
  const { user, loading, refreshProfile } = usePersonalSettings()
  const refresh = useCallback(async () => {
    await refreshProfile()
  }, [refreshProfile])
  const checkinEnabled = useStatusStore((s) => s.status?.checkin_enabled)
  const { runSecure, verifyVisible, handleSuccess, handleCancel } = useSecureVerification()

  const [twoFaEnabled, setTwoFaEnabled] = useState(false)
  const [passkeyEnabled, setPasskeyEnabled] = useState(false)

  const loadSecurityFlags = useCallback(async () => {
    try {
      const [twoFa, passkey] = await Promise.all([get2FAStatusApi(), getPasskeyStatusApi()])
      setTwoFaEnabled(!!twoFa.enabled)
      setPasskeyEnabled(!!passkey.enabled)
    } catch { /* ignore */ }
  }, [])

  useEffect(() => {
    void loadSecurityFlags()
  }, [loadSecurityFlags, user?.id])

  return (
    <div className="space-y-4">
      <AccountOverviewCard
        user={user}
        loading={loading}
        twoFaEnabled={twoFaEnabled}
        passkeyEnabled={passkeyEnabled}
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={16}>
          <div className="space-y-4">
            <ProfileCard user={user} onUpdated={refresh} runSecure={runSecure} />
            <OAuthBindingsCard runSecure={runSecure} onRefresh={refresh} />
            <NotificationSettingsCard rawSetting={user?.setting} onSaved={refresh} />
            {checkinEnabled && <CheckinAffiliateCard user={user} />}
          </div>
        </Col>
        <Col xs={24} xl={8}>
          <div className="space-y-4">
            <SecurityCenterCard
              runSecure={runSecure}
              twoFaEnabled={twoFaEnabled}
              passkeyEnabled={passkeyEnabled}
              onSecurityChange={() => void loadSecurityFlags()}
            />
            <SystemAccessTokenCard runSecure={runSecure} />
            <DangerZoneCard runSecure={runSecure} />
          </div>
        </Col>
      </Row>

      <SecureVerifyModal
        visible={verifyVisible}
        onCancel={handleCancel}
        onSuccess={handleSuccess}
        title={t('该操作涉及敏感信息，请完成安全验证')}
      />
    </div>
  )
}
